interface DisplayParticipant {
  providerSessionId: string
}

interface DisplayEvent {
  event?: string
}

interface QueuedDisplayEvent<P, E, U> {
  participant: P
  event: E
  update: U
}

type DisplayEmitter<P, E, U> = (participant: P, event: E, update: U) => void

const SOURCE_EVENTS = new Set(['transcript.partial', 'transcript.final'])
const OUTPUT_EVENTS = new Set(['translation.partial', 'translation.final', 'translation.batch'])

/**
 * Serializes concurrent participant streams into one stable speaker order.
 *
 * Input advances only after the active participant emits `transcript.final`.
 * Output follows the exact same source-turn order independently, so a faster
 * translation socket cannot render speaker B before speaker A's translation.
 */
export class ParticipantTranscriptDisplayQueue<
  P extends DisplayParticipant,
  E extends DisplayEvent,
  U,
> {
  private readonly emit: DisplayEmitter<P, E, U>
  private activeSourceSpeaker: string | null = null
  private readonly sourceBacklog: Array<QueuedDisplayEvent<P, E, U>> = []
  private readonly outputSpeakerOrder: string[] = []
  private readonly outputBacklog: Array<QueuedDisplayEvent<P, E, U>> = []

  /** @param emit Receives events only after their Input or Output lane becomes visible. */
  constructor(emit: DisplayEmitter<P, E, U>) {
    this.emit = emit
  }

  /**
   * Adds one participant event, emitting it immediately only when its speaker
   * currently owns the corresponding Input or Output lane.
   */
  push(participant: P, event: E, update: U): void {
    const queued = { participant, event, update }
    const kind = event.event
    if (kind && SOURCE_EVENTS.has(kind)) {
      this.pushSource(queued)
      return
    }
    if (kind && OUTPUT_EVENTS.has(kind)) {
      if (this.outputSpeakerOrder.length === 0 && this.activeSourceSpeaker === null) {
        this.emit(participant, event, update)
        return
      }
      this.outputBacklog.push(queued)
      this.flushOutput()
      return
    }
    this.emit(participant, event, update)
  }

  /** Clears every active lane and buffered event before a new native session starts. */
  reset(): void {
    this.activeSourceSpeaker = null
    this.sourceBacklog.length = 0
    this.outputSpeakerOrder.length = 0
    this.outputBacklog.length = 0
  }

  /** Routes a source event to the active speaker or buffers it for the next turn. */
  private pushSource(item: QueuedDisplayEvent<P, E, U>): void {
    const speaker = item.participant.providerSessionId
    if (this.activeSourceSpeaker === null) {
      this.startSourceTurn(item)
      return
    }
    if (this.activeSourceSpeaker !== speaker) {
      this.sourceBacklog.push(item)
      return
    }

    this.emit(item.participant, item.event, item.update)
    if (item.event.event === 'transcript.final') this.finishSourceTurn()
  }

  /** Opens a source turn and registers the same speaker as the next Output owner. */
  private startSourceTurn(item: QueuedDisplayEvent<P, E, U>): void {
    const speaker = item.participant.providerSessionId
    this.activeSourceSpeaker = speaker
    this.outputSpeakerOrder.push(speaker)
    this.flushOutput()
    this.emit(item.participant, item.event, item.update)
    if (item.event.event === 'transcript.final') this.finishSourceTurn()
  }

  /** Hands Input to the oldest waiting speaker and drains that speaker's buffered events. */
  private finishSourceTurn(): void {
    this.activeSourceSpeaker = null
    while (this.activeSourceSpeaker === null && this.sourceBacklog.length > 0) {
      const first = this.sourceBacklog.shift()
      if (!first) return
      this.startSourceTurn(first)

      while (this.activeSourceSpeaker !== null) {
        const nextIndex = this.sourceBacklog.findIndex(
          (item) => item.participant.providerSessionId === this.activeSourceSpeaker
        )
        if (nextIndex === -1) return
        const [next] = this.sourceBacklog.splice(nextIndex, 1)
        this.emit(next.participant, next.event, next.update)
        if (next.event.event === 'transcript.final') this.activeSourceSpeaker = null
      }
    }
  }

  /** Emits Output events only for the next source turn, retaining later speakers in-place. */
  private flushOutput(): void {
    while (this.outputSpeakerOrder.length > 0) {
      const expectedSpeaker = this.outputSpeakerOrder[0]
      const nextIndex = this.outputBacklog.findIndex(
        (item) => item.participant.providerSessionId === expectedSpeaker
      )
      if (nextIndex === -1) return

      const [next] = this.outputBacklog.splice(nextIndex, 1)
      this.emit(next.participant, next.event, next.update)
      if (next.event.event === 'translation.final' || next.event.event === 'translation.batch') {
        this.outputSpeakerOrder.shift()
      }
    }
  }
}
